import { Link } from "@inertiajs/react"
import { Calendar, ChevronDown, Clock, Eye, FileText, Library, MoreHorizontal, Package, RotateCcw, Users } from "lucide-react"

import StatusBadge from "./StatusBadge"
import { Avatar, AvatarStack } from "./Avatar"
import { useCountdown } from "./insider/Countdown"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { cn } from "@/lib/utils"
import { DOWNLOAD_LABELS, downloadFiles, downloadIsMenu, resolveHeader } from "./order/actions"
import { fileSize } from "@/lib/fileSize"

// Order type -> the small label printed on the cover spine + the stat-strip
// caption for the revision cell (Concepts / Illustrations / Proofs / Chapters
// read more naturally than "Revisions" for non-writing work).
const TYPE_LABEL = {
  manuscript: "Manuscript",
  cover: "Cover Design",
  illustration: "Illustration",
  formatting: "Formatting",
  narration: "Audiobook",
}

const REV_CAPTION = {
  manuscript: "Revisions",
  cover: "Concepts",
  illustration: "Illustrations",
  formatting: "Proofs",
  narration: "Chapters",
}

function initialsOf(name) {
  return (
    (name || "")
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word[0])
      .join("")
      .toUpperCase() || "?"
  )
}

// Renders one resolved action as the right element: a download link, an
// in-app navigation link, a modal trigger, or a tab jump.
function ActionButton({ action, order, onOpenModal, onSelectTab, primary = false }) {
  const { Icon } = action
  // `max-[861px]:flex-1` is the header's wrapped-cluster rule (see OrderHeader):
  // it only bites once the cluster is on its own full-width line.
  const className = cn("gap-[7px] max-[861px]:flex-1", primary && "font-bold")

  if (action.href) {
    return (
      <Button asChild variant={primary ? "default" : "outline"} size={primary ? "default" : "sm"} className={className}>
        <Link href={action.href}>
          <Icon className="size-4" strokeWidth={primary ? 2.2 : 2} />
          {action.label}
        </Link>
      </Button>
    )
  }

  if (action.download && downloadIsMenu(order)) {
    return <DownloadMenu action={action} files={downloadFiles(order)} primary={primary} className={className} />
  }

  if (action.download) {
    const file = order.deliverables?.download
    return (
      <Button asChild variant={primary ? "default" : "outline"} size={primary ? "default" : "sm"} className={className}>
        <a href={file?.url} download={file?.fileName}>
          <Icon className="size-4" strokeWidth={2} />
          {action.label}
        </a>
      </Button>
    )
  }

  const onClick = action.modal ? () => onOpenModal(action.modal) : () => onSelectTab(action.tab)

  return (
    <Button
      variant={primary ? "default" : "outline"}
      size={primary ? "default" : "sm"}
      className={className}
      onClick={onClick}
    >
      <Icon className="size-4" strokeWidth={primary ? 2.2 : 2} />
      {action.label}
    </Button>
  )
}

// Download as a menu, for an order with more than its one archive to hand
// over: each file the customer may take, named and sized, as a real download
// link. The same DS DropdownMenu as the ••• overflow below, for the same
// reason — it portals out of the header card that would otherwise clip it.
// A copyright form that has not been made yet arrives with no file name: its
// link makes the form and redirects to it, so it navigates instead of
// carrying a `download` name.
function DownloadMenu({ action, files, primary, className }) {
  const { Icon } = action

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant={primary ? "default" : "outline"} size={primary ? "default" : "sm"} className={className}>
          <Icon className="size-4" strokeWidth={2} />
          {action.label}
          <ChevronDown className="size-3.5" strokeWidth={2.2} />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        sideOffset={6}
        className="w-[288px] max-w-[calc(100vw-24px)] rounded-xl border-border bg-popover p-1.5 shadow-[0_18px_44px_-18px_rgba(0,0,0,0.45)]"
      >
        {files.map((file) => (
          <DropdownMenuItem
            key={file.url}
            asChild
            className="gap-[11px] rounded-lg px-3 py-[9px] text-[13px] font-medium text-foreground focus:bg-accent focus:text-foreground"
          >
            <a href={file.url} download={file.fileName || undefined} title={file.fileName || undefined}>
              <FileText className="size-[16px] shrink-0 text-ink-3" strokeWidth={1.9} />
              <span className="min-w-0 flex-1">{DOWNLOAD_LABELS[file.kind] ?? file.fileName}</span>
              <span className="shrink-0 text-[11.5px] font-normal text-ink-3 tabular-nums">{fileSize(file.fileSize)}</span>
            </a>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

// The ••• overflow: a kebab button + a right-aligned menu of grouped actions,
// each filtered to what's allowed.
//
// Built on the DS DropdownMenu, which portals to the body. The hand-rolled
// version was an absolutely-positioned popover rendered INSIDE the order-header
// card, and that card clips its descendants with `overflow-hidden` — so the menu
// was cut off at the card's edge and its lower items could not be read or
// clicked at all. No z-index escapes a clipping ancestor; only leaving the
// subtree does (AU-02). Radix also owns the open state, outside-click and Escape
// dismissal, the menu/menuitem ARIA, and a max-height bound against the
// available viewport space — all of which this component previously hand-rolled.
// Mirrors PersonMenu, the same pattern on the Access page (Hallmark §5d).
function ActionMenu({ groups, order, onOpenModal, onSelectTab }) {
  if (!groups.length) return null

  const run = (action) => {
    if (action.download) {
      const file = order.deliverables?.download
      if (file?.url) window.open(file.url, "_blank", "noopener")
    } else if (action.modal) {
      onOpenModal(action.modal)
    } else if (action.tab) {
      onSelectTab(action.tab)
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" aria-label="More actions">
          <MoreHorizontal className="size-4" strokeWidth={2} />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        sideOffset={6}
        className="w-[224px] rounded-xl border-border bg-popover p-1.5 shadow-[0_18px_44px_-18px_rgba(0,0,0,0.45)]"
      >
        {groups.map((group, gi) => (
          <DropdownMenuGroup key={gi}>
            {gi > 0 && <DropdownMenuSeparator />}
            {group.map((action) => (
              <DropdownMenuItem
                key={action.key}
                // Deferred a tick: several of these open a Dialog, and handing
                // focus to a dialog while the menu is still closing leaves both
                // fighting for it. Letting the menu finish first is the cheap fix.
                onSelect={() => setTimeout(() => run(action), 0)}
                className={cn(
                  "gap-[11px] rounded-lg px-3 py-[9px] text-[13px] font-medium",
                  action.danger
                    ? "text-primary focus:bg-[var(--red-soft)] focus:text-primary"
                    : "text-foreground focus:bg-accent focus:text-foreground"
                )}
              >
                <action.Icon
                  className={cn("size-[16px] shrink-0", action.danger ? "text-primary" : "text-ink-3")}
                  strokeWidth={1.9}
                />
                <span>{action.label}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuGroup>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

// Metric-strip cell chrome, shared by the three StatCells and the inline "Your
// team" cell so all four wrap identically (Switchback, P8-H3).
//
// Each width band owns its own dividers over a NON-OVERLAPPING media range,
// because Tailwind v4 emits arbitrary-selector variants (`[&:nth-child(…)]`)
// after every plain media variant regardless of authoring order — two bands
// fighting over one border would not resolve the way they read here.
//   ≥861  4-across: a right border between cells.
//   561–860  2×2: right border on the odd cells, bottom border on the first row.
//   ≤560  single column: no right borders, bottom border on all but the last.
// EVERY breakpoint literal in this file is the ruled edge PLUS ONE, and that is
// not a typo. pm.css rules these bands `@media (max-width:860px)` (:370) and
// `@media (max-width:560px)` (:415), both INCLUSIVE of the named width, but
// Tailwind v4 compiles `max-[N]` as `(width < N)` and `min-[N]` as
// `(width >= N)`. The naive `max-[860px]` therefore stops at 859 while its
// counterpart `min-[861px]` starts at 861, so 860 matched NEITHER band —
// measured on this file's base commit at exactly 860: the strip was still a
// four-across flex row with no cell dividers and all four values truncated to
// stubs ("Illustra…", "1 …", "0 …", "A…"); and at exactly 560 the cells fell
// back to the base desktop `px-[26px] py-4` with no borders at all.
// `max-[861px]` is `width <= 860`, exactly complementary to `min-[861px]`;
// `max-[561px]` is `width <= 560`, exactly complementary to `min-[561px]`. Same
// convention as profile/StatStrip.jsx :36 and post/PriceRail.jsx :168.
// `min-w-0 flex-auto` is load-bearing, not decoration, and BOTH halves matter.
//   · `min-w-0` — a flex item's default `min-width:auto` refuses to shrink below
//     its content, so in the ≥861 FOUR-ACROSS band the cells held their
//     intrinsic width and the last one was CUT OFF by the card's
//     `overflow-hidden`; `main` never widens, so every overflow probe is blind
//     to it. Measured before this fix at 1024 with the shell rail back on
//     screen: illustration 22064's "Placed / Aug 1, 2026" cell ran to right=1105
//     against a card clip edge of 998 (107px lost), cover 22063 and manuscript
//     22054 9px each, and at a 860 viewport (main = 636px) the third AND fourth
//     cells were both gone — that last reading predates the literal correction
//     above, taken when 860 still fell into this band; 860 is 2×2 now.
//     Releasing the floor lets the cell shrink and the
//     value's existing `truncate` ellipsise, which is what the strip intends.
//   · `flex-auto` (`1 1 auto`), NOT `flex-1` (`1 1 0%`) — a zero basis makes the
//     four cells divide the row EQUALLY, so the longest value ellipsises even at
//     widths where it would have fitted: measured at 1200 and 1280, "Illustration
//     Package" truncated to a 125px box against its own 176px of text. A content
//     basis that is still allowed to shrink gives the full string back at
//     1150-1440 and keeps the ellipsis-instead-of-clip behaviour at 860-1112
//     (measured: no cell past the card edge at 860/861/900/1000/1024/1112 on any
//     of the three probeable orders, narrowest cell 124px).
const METRIC_CELL = cn(
  "flex min-w-0 flex-auto items-center gap-[13px] border-border px-[26px] py-4",
  "min-[861px]:border-r min-[861px]:last:border-r-0",
  "min-[561px]:max-[861px]:px-[18px] min-[561px]:max-[861px]:py-[14px]",
  "min-[561px]:max-[861px]:odd:border-r min-[561px]:max-[861px]:[&:nth-child(-n+2)]:border-b",
  "max-[561px]:px-4 max-[561px]:py-3 max-[561px]:[&:not(:last-child)]:border-b"
)

function StatCell({ Icon, caption, value }) {
  return (
    <div className={METRIC_CELL}>
      <span className="grid size-10 shrink-0 place-items-center rounded-[10px] border border-border bg-secondary">
        <Icon className="size-[19px] text-muted-foreground" strokeWidth={1.8} />
      </span>
      <div className="min-w-0">
        <div className="text-[11px] font-semibold text-ink-3">{caption}</div>
        <div className="mt-0.5 truncate font-display text-base font-bold">{value}</div>
      </div>
    </div>
  )
}

// Ticks down to the moment work under review approves itself: each unit over
// its label, split by colons. The job that approves runs hourly, so the clock
// can sit at zero for up to an hour first. Between 861 and 1150 the strip is
// four-across beside the sidebar: a clock cannot ellipsise like the other
// values, so there it drops its icon, tightens, and keeps its width while the
// other cells give way (measured clipping 18px at 861 without this).
const COUNTDOWN_UNITS = [["d", "Days"], ["h", "Hrs"], ["m", "Mins"], ["s", "Secs"]]

function AutoApproveStat({ at }) {
  const left = useCountdown(at)
  return (
    <div className={cn(METRIC_CELL, "min-[861px]:max-[1150px]:shrink-0 min-[861px]:max-[1150px]:px-4")}>
      <span className="grid size-10 shrink-0 place-items-center rounded-[10px] border border-border bg-secondary min-[861px]:max-[1150px]:hidden">
        <Clock className="size-[19px] text-muted-foreground" strokeWidth={1.8} />
      </span>
      <div className="min-w-0">
        <div className="truncate text-[11px] font-semibold text-ink-3">Auto-approves in</div>
        {left && (
          <div className="mt-1 flex items-start gap-1.5 min-[861px]:max-[1150px]:gap-1">
            {COUNTDOWN_UNITS.map(([key, label], i) => (
              <div key={key} className="flex items-start gap-1.5 min-[861px]:max-[1150px]:gap-1">
                {i > 0 && <span className="font-display text-[19px] leading-none font-bold text-ink-3 min-[861px]:max-[1150px]:text-base">:</span>}
                <div className="text-center">
                  <div
                    className="font-display text-[19px] leading-none font-bold tabular-nums min-[861px]:max-[1150px]:text-base"
                    data-testid={`countdown-${key}`}
                  >
                    {String(left[key]).padStart(2, "0")}
                  </div>
                  <div className="mt-1 text-[8.5px] font-semibold tracking-[0.06em] text-ink-3 uppercase min-[861px]:max-[1150px]:tracking-normal">{label}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function MetaItem({ Icon, children }) {
  return (
    <div className="flex items-center gap-[7px] text-[12.5px] text-muted-foreground">
      {Icon && <Icon className="size-[15px] text-ink-3" strokeWidth={1.9} />}
      <span>{children}</span>
    </div>
  )
}

// The order-detail header: cover + identity + the phase-driven action cluster
// ([•••] · secondary… · Primary), with a stat strip across the foot. Mirrors
// orderHeader() in the design prototype, but the action set is resolved from the
// order's phase + real permissions (components/tuw/order/actions).
export default function OrderHeader({ order, onOpenModal, onSelectTab }) {
  const {
    title,
    projectName,
    subtitle,
    type,
    status,
    placedDate,
    author,
    orderNumber,
    projectNumber,
    package: pkg,
    team = [],
    manager = null,
    revisions = {},
    dueDate,
    autoApproveAt,
  } = order

  const { primary, secondary, menu } = resolveHeader(order)
  // The manager on a managed order counts as someone on the project (AU-41):
  // before, a managed order with no freelancer yet read "Not assigned yet"
  // even though a manager was already working it.
  const people = manager ? [...team, manager] : team
  const teamValue = people.length
    ? `${people.length} ${people.length === 1 ? "member" : "members"}`
    : "Not assigned yet"

  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card">
      {/* At 860 and below the row wraps: cover + identity keep their pairing on
          the first line and the action cluster claims the second one. */}
      <div className="flex gap-[22px] px-[26px] py-6 max-[861px]:flex-wrap max-[861px]:gap-4 max-[861px]:px-[18px] max-[861px]:pt-[18px] max-[861px]:pb-4 max-[561px]:gap-[13px] max-[561px]:px-[14px] max-[561px]:pt-4 max-[561px]:pb-[14px]">
        {/* cover spine — A DELIBERATE DEVIATION FROM THE DESIGN SOURCE.
            design/spec/shared/pm.css draws the type label as an ABSOLUTE box
            (:124 `.ohead-cover .cb{position:absolute;bottom:12px;left:0;right:0}`)
            over a title that is a plain centred flex child (:123), inside a
            fixed 108×150 / 78×108 `overflow:hidden` box (:122, :417). Two
            layers, so a title taller than the room above the label paints
            STRAIGHT THROUGH it instead of being stopped by it — measured at 365
            on this tree: 12.8px of overprint on order 22064 and 10.4px on 22063.
            The prototype's sample titles are short, so it never shows there.
            The ruled intent of the box is `overflow:hidden` — CLIPPING is
            sanctioned, OVERPRINTING is not — so the label becomes a FLOW
            sibling: it reserves its own height whatever number of lines it
            wraps to, and the title takes what is left (`min-h-0 flex-1`) and
            clips against it (`max-h-full` + `overflow-hidden`, so the cut is at
            the BOTTOM and the opening lines survive). Structural, not a
            phone-only clamp: no title can reach the label at any width.
            The label's own geometry is unchanged to the pixel — `-mx-[14px]`
            re-creates `left:0;right:0` (the PADDING box, so "Cover Design"
            still wraps to two lines at 78px instead of four inside the 48px
            content box) and `-mb-0.5` re-creates `bottom:12px`. What the
            deviation costs: a SHORT title now centres in the room above the
            label rather than in the whole box, ~7px higher at 78×108. The title
            element's typography is untouched (pm.css :123 rules it; a
            `w-full break-words` line here was reverted last slice for splitting
            words mid-word at every width). */}
        <div className="relative flex h-[150px] w-[108px] shrink-0 flex-col overflow-hidden rounded-[10px] border border-line-2 bg-gradient-to-br from-[#2A2622] to-[#14110F] p-[14px] text-center text-white shadow-[0_6px_20px_-8px_rgba(0,0,0,0.35)] max-[561px]:h-[108px] max-[561px]:w-[78px]">
          <div className="flex min-h-0 flex-1 items-center justify-center">
            {/* `min-w-min` is load-bearing: a flex item with `overflow` other
                than `visible` has an automatic minimum size of ZERO (flexbox
                §4.5), so the `overflow-hidden` that buys the vertical clip also
                collapsed this box from its min-content 82px to the 48px content
                box and cut "Canonical" 32px short instead of the ruled 2px.
                Restoring the min-content floor keeps the horizontal drawing —
                and the spine's own clip edge — exactly as pm.css rules it. */}
            <div className="max-h-full min-w-min overflow-hidden font-display text-[15px] leading-[1.15] font-semibold">{title}</div>
          </div>
          <div className="-mx-[14px] -mb-0.5 shrink-0 pt-1 text-[8.5px] tracking-[0.18em] uppercase opacity-65">
            {TYPE_LABEL[type] ?? "Order"}
          </div>
        </div>

        {/* identity */}
        <div className="min-w-0 flex-1">
          {/* The bundle this order belongs to; a standalone order has no
              second name to show (UPDA-101). */}
          {projectName && <div className="text-xs font-semibold text-ink-3">{projectName}</div>}
          <div className="mt-[3px] flex flex-wrap items-center gap-3">
            <h1 className="font-display text-[27px] font-bold tracking-[-0.5px] max-[561px]:text-[20px] max-[561px]:tracking-[-0.4px]">{title}</h1>
            <StatusBadge tier={status.tier} label={status.label} attention={status.attention} />
          </div>
          {subtitle && (
            <div className="mt-[3px] text-[13.5px] text-muted-foreground">{subtitle}</div>
          )}

          <div className="mt-[15px] flex flex-wrap gap-x-[22px] gap-y-2 max-[561px]:mt-[11px] max-[561px]:gap-x-4 max-[561px]:gap-y-[7px]">
            <MetaItem Icon={Calendar}>
              Placed <b className="font-semibold text-foreground">{placedDate}</b>
            </MetaItem>
            <MetaItem>
              <span className="inline-flex items-center gap-[7px]">
                <Avatar initials={initialsOf(author)} name={author} size={22} me />
                <span>
                  by <b className="font-semibold text-foreground">{author}</b>
                </span>
              </span>
            </MetaItem>
            <MetaItem Icon={FileText}>
              Order <b className="font-semibold text-foreground">#{orderNumber}</b>
            </MetaItem>
            <MetaItem Icon={Library}>
              Project <b className="font-semibold text-foreground">{projectNumber}</b>
            </MetaItem>
          </div>
        </div>

        {/* action cluster: [•••] secondary… Primary. At 860 and below it takes
            the whole second line and the buttons stretch to fill it; the kebab
            keeps its natural width so it reads as chrome, not an action.
            It wraps as well as stretches (prototype `.act-row`): buttons are
            whitespace-nowrap, so the four-control `final` phase cannot fit one
            365px line and would otherwise be clipped by the card's overflow. */}
        <div className="flex shrink-0 items-center gap-[9px] self-start max-[861px]:basis-full max-[861px]:flex-wrap">
          {primary ? (
            <>
              <ActionMenu groups={menu} order={order} onOpenModal={onOpenModal} onSelectTab={onSelectTab} />
              {secondary.map((action) => (
                <ActionButton
                  key={action.key}
                  action={action}
                  order={order}
                  onOpenModal={onOpenModal}
                  onSelectTab={onSelectTab}
                />
              ))}
              <ActionButton
                action={primary}
                order={order}
                onOpenModal={onOpenModal}
                onSelectTab={onSelectTab}
                primary
              />
            </>
          ) : (
            // A shared order (AU-44): the cluster's place is taken by a plain
            // read-only marker, so the header never looks like it lost its
            // buttons. The strip above the header says whose order it is.
            <span
              data-testid="read-only-marker"
              className="inline-flex items-center gap-[7px] rounded-lg border border-border bg-secondary px-3 py-[7px] text-[12px] font-semibold text-ink-3"
            >
              <Eye className="size-4" strokeWidth={1.9} />
              Read-only
            </span>
          )}
        </div>
      </div>

      {/* stat strip: 4-across, 2×2 at ≤860, stacked at ≤560 */}
      <div className="flex border-t border-border max-[861px]:grid min-[561px]:max-[861px]:grid-cols-2 max-[561px]:grid-cols-1">
        <StatCell Icon={Package} caption="Package" value={pkg} />
        <div className={METRIC_CELL}>
          {people.length ? (
            <AvatarStack people={people} size={30} />
          ) : (
            <span className="grid size-10 shrink-0 place-items-center rounded-[10px] border border-border bg-secondary">
              <Users className="size-[19px] text-muted-foreground" strokeWidth={1.8} />
            </span>
          )}
          <div className="min-w-0">
            <div className="text-[11px] font-semibold text-ink-3">Your team</div>
            <div className="mt-0.5 truncate font-display text-base font-bold">{teamValue}</div>
          </div>
        </div>
        <StatCell Icon={RotateCcw} caption={REV_CAPTION[type] ?? "Revisions"} value={revisions.label} />
        {/* While work is in review, the last stat counts down to its automatic
            approval in place of the due or placed date. */}
        {autoApproveAt ? (
          <AutoApproveStat at={autoApproveAt} />
        ) : (
          <StatCell
            Icon={Calendar}
            caption={dueDate ? "Due date" : "Placed"}
            value={dueDate ?? placedDate}
          />
        )}
      </div>
    </div>
  )
}
